const DRAWING_DIM_FONT_PX = 10;
const DRAWING_DIM_FONT_PX_COMPACT = 10;

function formatDrawingDimension(value) {
  const num = Math.round(Number(value));
  if (!num || Number.isNaN(num)) return '';
  return String(num);
}

function stripMmFromLabel(raw) {
  return String(raw || '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s*mm\s*$/i, '')
    .trim();
}

function isPerPartForeignObject(foreignObject) {
  const w = parseFloat(foreignObject.getAttribute('width') || '0');
  return w > 0 && w <= 55;
}

function isMainDimensionForeignObject(foreignObject) {
  if (isPerPartForeignObject(foreignObject)) return false;
  if (
    Array.from(foreignObject.querySelectorAll('span')).some((span) =>
      (span.textContent || '').toLowerCase().includes('mm')
    )
  ) {
    return true;
  }
  const width = parseFloat(foreignObject.getAttribute('width') || '0');
  return width >= 90 && !!foreignObject.querySelector('input[type="number"]');
}

function getForeignObjectLabel(foreignObject) {
  const input = foreignObject.querySelector('input');
  const value = (input?.getAttribute('value') || input?.value || '').trim();
  if (value) return formatDrawingDimension(value);

  const spans = Array.from(foreignObject.querySelectorAll('span'))
    .map((span) => span.textContent?.trim())
    .filter(Boolean)
    .filter((text) => text.toLowerCase() !== 'mm');
  if (spans.length) return formatDrawingDimension(spans.join(' '));

  return formatDrawingDimension(stripMmFromLabel(foreignObject.textContent));
}

function isDimensionLabelText(textEl) {
  if (textEl.getAttribute('data-dimension-label') === 'true') return true;
  const content = stripMmFromLabel(textEl.textContent);
  if (!content || content.length < 2) return false;
  return /^\d+$/.test(content);
}

function getTextLabel(textEl) {
  return formatDrawingDimension(stripMmFromLabel(textEl.textContent));
}

function svgPointToWrap(svgEl, wrapEl, svgX, svgY) {
  if (!svgEl || !wrapEl) return null;

  const pt = svgEl.createSVGPoint();
  pt.x = svgX;
  pt.y = svgY;
  const ctm = svgEl.getScreenCTM();
  if (!ctm) return null;

  const screen = pt.matrixTransform(ctm);
  const wrapRect = wrapEl.getBoundingClientRect();
  return {
    left: screen.x - wrapRect.left,
    top: screen.y - wrapRect.top,
  };
}

function elementPositionOnWrap(element, svgEl, wrapEl, axis) {
  const rect = element.getBoundingClientRect();
  const wrapRect = wrapEl.getBoundingClientRect();

  if (rect.width >= 1 && rect.height >= 1) {
    const top = rect.top - wrapRect.top + rect.height / 2;
    if (axis === 'height') {
      return { left: rect.left - wrapRect.left + 1, top, align: 'start' };
    }
    return {
      left: rect.left - wrapRect.left + rect.width / 2,
      top,
      align: 'center',
    };
  }

  if (element.tagName === 'foreignObject') {
    const x = parseFloat(element.getAttribute('x') || '0');
    const y = parseFloat(element.getAttribute('y') || '0');
    const w = parseFloat(element.getAttribute('width') || '0');
    const h = parseFloat(element.getAttribute('height') || '0');
    if (axis === 'height') {
      const pt = svgPointToWrap(svgEl, wrapEl, x + 2, y + h / 2);
      return pt ? { ...pt, align: 'start' } : null;
    }
    const pt = svgPointToWrap(svgEl, wrapEl, x + w / 2, y + h / 2);
    return pt ? { ...pt, align: 'center' } : null;
  }

  if (element.tagName === 'text') {
    const x = parseFloat(element.getAttribute('x') || '0');
    const y = parseFloat(element.getAttribute('y') || '0');
    const anchor = element.getAttribute('text-anchor') || 'middle';
    const pt = svgPointToWrap(svgEl, wrapEl, x, y);
    if (!pt) return null;
    if (axis === 'height' && anchor === 'start') {
      return { ...pt, align: 'start' };
    }
    return { ...pt, align: axis === 'height' ? 'start' : 'center' };
  }

  return null;
}

function collectMeasuredCandidates(svgEl, wrapEl) {
  const candidates = [];

  svgEl.querySelectorAll('foreignObject').forEach((foreignObject, index) => {
    if (!isMainDimensionForeignObject(foreignObject)) return;

    const text = getForeignObjectLabel(foreignObject);
    if (!text) return;

    candidates.push({
      id: `fo-${index}`,
      text,
      element: foreignObject,
    });
  });

  svgEl.querySelectorAll('text').forEach((textEl, index) => {
    if (!isDimensionLabelText(textEl)) return;

    const text = getTextLabel(textEl);
    if (!text) return;

    candidates.push({
      id: `text-${index}`,
      text,
      element: textEl,
    });
  });

  return candidates;
}

function candidateToOverlay(candidate, svgEl, wrapEl, axis, id, text) {
  const position = elementPositionOnWrap(candidate.element, svgEl, wrapEl, axis);
  if (!position) return null;
  return { id, text, left: position.left, top: position.top, align: position.align };
}

function pickCandidateForDimension(candidates, targetText, axis, svgEl, wrapEl) {
  const matches = candidates.filter((c) => c.text === targetText);
  if (!matches.length) return null;

  if (matches.length === 1) return matches[0];

  return matches.reduce((best, current) => {
    const bestPos = elementPositionOnWrap(best.element, svgEl, wrapEl, axis);
    const currentPos = elementPositionOnWrap(current.element, svgEl, wrapEl, axis);
    if (!bestPos) return current;
    if (!currentPos) return best;
    if (axis === 'width') {
      return currentPos.top > bestPos.top ? current : best;
    }
    return currentPos.left > bestPos.left ? current : best;
  });
}

function buildHeuristicOverlays(svgEl, wrapEl, configWidth, configHeight) {
  const widthText = formatDrawingDimension(configWidth);
  const heightText = formatDrawingDimension(configHeight);
  if (!widthText && !heightText) return [];

  const wrapRect = wrapEl.getBoundingClientRect();
  const svgRect = svgEl.getBoundingClientRect();
  if (wrapRect.width < 1 || svgRect.width < 1) return [];

  const offsetX = svgRect.left - wrapRect.left;
  const offsetY = svgRect.top - wrapRect.top;
  const overlays = [];

  if (widthText) {
    overlays.push({
      id: 'dim-width',
      text: widthText,
      left: offsetX + svgRect.width * 0.5,
      top: offsetY + svgRect.height * 0.96,
      align: 'center',
    });
  }

  if (heightText) {
    overlays.push({
      id: 'dim-height',
      text: heightText,
      left: offsetX + svgRect.width * 0.88,
      top: offsetY + svgRect.height * 0.5,
      align: 'start',
    });
  }

  return overlays;
}

/** Build fixed-size HTML overlays aligned to the SVG dimension labels */
export function measureLiveDimensionOverlays(wrapEl, svgEl, configWidth, configHeight) {
  const widthText = formatDrawingDimension(configWidth);
  const heightText = formatDrawingDimension(configHeight);
  if (!widthText && !heightText) return [];
  if (!wrapEl || !svgEl) return [];

  const candidates = collectMeasuredCandidates(svgEl, wrapEl);
  const overlays = [];

  const widthCandidate = pickCandidateForDimension(candidates, widthText, 'width', svgEl, wrapEl);
  const heightCandidate = pickCandidateForDimension(candidates, heightText, 'height', svgEl, wrapEl);

  if (widthText) {
    overlays.push(
      widthCandidate
        ? candidateToOverlay(widthCandidate, svgEl, wrapEl, 'width', 'dim-width', widthText)
        : buildHeuristicOverlays(svgEl, wrapEl, configWidth, configHeight).find((o) => o.id === 'dim-width')
    );
  }

  if (heightText) {
    overlays.push(
      heightCandidate
        ? candidateToOverlay(heightCandidate, svgEl, wrapEl, 'height', 'dim-height', heightText)
        : buildHeuristicOverlays(svgEl, wrapEl, configWidth, configHeight).find((o) => o.id === 'dim-height')
    );
  }

  return overlays.filter(Boolean);
}

export function stripSvgDimensionLabels(svg) {
  if (!svg) return;

  svg.querySelectorAll('foreignObject').forEach((foreignObject) => {
    if (
      isMainDimensionForeignObject(foreignObject) ||
      isPerPartForeignObject(foreignObject) ||
      foreignObject.querySelector('input[type="number"]')
    ) {
      foreignObject.remove();
    }
  });

  svg.querySelectorAll('text[data-dimension-label="true"]').forEach((textEl) => {
    textEl.remove();
  });
}

export function getDrawingDimFontPx(compact = false) {
  return compact ? DRAWING_DIM_FONT_PX_COMPACT : DRAWING_DIM_FONT_PX;
}

export { DRAWING_DIM_FONT_PX, DRAWING_DIM_FONT_PX_COMPACT };
